import type { AccesoDePlataforma, EstadoDeSuscripcion, FichaDeNegocio, FiltroNegocios, MiembroDePlataforma, NegocioDePlataforma, PartnerDePlataforma, SuscripcionDeNegocio } from '../../api/ops/backoffice';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import { conY } from '../../logica/acceso';
import { nombreBloque } from '../../pantallas/plan/logica';
import { textoTipo } from '../../partners/logica';

/**
 * Lógica de **Negocios** del backoffice (1.20), sin nada visual: textos de la lista y de la ficha, filtros y validación
 * de las ventanas, con los textos del prototype (`1.20-1.22-backoffice.html`). Las cifras salen de la API (G184): el
 * panel no suma ni calcula cuotas.
 */

/** Nombre del estado de la suscripción en la ficha (prototipo `ST`; los que el prototipo no tiene, con el mismo estilo). */
export const ESTADOS: Readonly<Record<EstadoDeSuscripcion, string>> = {
    active: 'Activo',
    trialing: 'En prueba',
    trial_expired: 'Prueba caducada',
    paused: 'Pausado',
    past_due: 'Impago',
    suspended: 'Suspendido',
    cancel_scheduled: 'Baja programada',
    cancelled: 'Baja',
    closed: 'Cerrado',
};

/** Chip de la lista: solo si pide actuar (componentes.md §17), como «Impago» y «Baja» del prototipo. */
export function chipDeLista(estado: EstadoDeSuscripcion): { texto: string; estado: EstadoChip } | null {
    return estado === 'past_due' || estado === 'suspended' || estado === 'cancelled' ? { texto: ESTADOS[estado], estado: 'problem' } : null;
}

/** Filtros de la lista (prototipo `FILTERS`): Todos · Activos · En prueba · Con problemas (`with_problems`, G184). */
export const FILTROS: readonly { etiqueta: string; filtro: Pick<FiltroNegocios, 'status' | 'with_problems'> }[] = [
    { etiqueta: 'Todos', filtro: {} },
    { etiqueta: 'Activos', filtro: { status: 'active' } },
    { etiqueta: 'En prueba', filtro: { status: 'trialing' } },
    { etiqueta: 'Con problemas', filtro: { with_problems: true } },
];

/** `?filtro=problemas` del prototipo abre «Con problemas». */
export function filtroDe(busqueda: URLSearchParams): number {
    const f = busqueda.get('filtro');
    return f === 'problemas' ? 3 : f === 'activos' ? 1 : f === 'prueba' ? 2 : 0;
}
export const CLAVES_FILTRO: readonly (string | null)[] = [null, 'activos', 'prueba', 'problemas'];

/** Siglas como el prototipo: sin artículos ni preposiciones («Chiringuito El Faro» → «CF»). */
export function siglas(nombre: string): string {
    return nombre
        .split(/\s+/)
        .filter((p) => p !== '' && !/^(el|la|los|las|de|del|y)$/i.test(p))
        .map((p) => p.charAt(0).toLocaleUpperCase('es'))
        .slice(0, 2)
        .join('');
}

const euros = (centimos: number): string => {
    const valor = centimos / 100;
    return valor.toLocaleString('es-ES', { minimumFractionDigits: Number.isInteger(valor) ? 0 : 2, maximumFractionDigits: 2, useGrouping: true });
};

/** Cuota de la lista: «146,25 €/mes», «58 €/mes»; sin cuota (prueba, baja), «—». */
export function cuota(fee: NegocioDePlataforma['monthly_fee']): string {
    return fee === null || fee === undefined || fee.amount === 0 ? '—' : `${euros(fee.amount)} €/mes`;
}

/** «Total al mes» de la pestaña Plan: «146,25 €» (sin IVA). */
export function totalAlMes(fee: NegocioDePlataforma['monthly_fee']): string {
    if (fee === null || fee === undefined || fee.amount === 0) {
        return '—';
    }
    const valor = fee.amount / 100;
    return `${valor.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** «03/2025»: mes y año del alta. */
export function clienteDesde(creado: string): string {
    return `${creado.slice(5, 7)}/${creado.slice(0, 4)}`;
}

export const personas = (n: number): string => (n === 1 ? '1 persona' : `${String(n)} personas`);

/** Una fila de la ficha (prototipo `L(k, v, sub, strong)`). */
export interface Linea {
    clave: string;
    valor: string;
    sub: string | null;
    fuerte: boolean;
}
const linea = (clave: string, valor: string, sub: string | null = null, fuerte = false): Linea => ({ clave, valor, sub, fuerte });

/** Línea bajo el nombre en la cabecera de la ficha: «Activo · Base + 6 bloques · cupón −25 %». */
export function lineaDeFicha(n: NegocioDePlataforma): string {
    return [ESTADOS[n.subscription_status], n.plan_summary ?? null].filter((x): x is string => x !== null && x !== '').join(' · ');
}

/** «2 avisos abiertos», «1 aviso abierto» (G1128: `open_alerts_count`); sin avisos, null. */
export function textoAvisos(n: { open_alerts_count?: number; has_open_alerts?: boolean }): string | null {
    const cuantos = n.open_alerts_count ?? (n.has_open_alerts === true ? null : 0);
    if (cuantos === null) {
        return 'Hay avisos abiertos';
    }
    if (cuantos <= 0) {
        return null;
    }
    return cuantos === 1 ? '1 aviso abierto' : `${String(cuantos)} avisos abiertos`;
}

/**
 * Pestaña **Resumen** (G138). El propietario sale con su nombre y, debajo, su email (G1128: `owner_name`); los avisos
 * abiertos, con cuántos son (`open_alerts_count`, G1128).
 */
export function lineasResumen(f: FichaDeNegocio): Linea[] {
    const avisos = textoAvisos(f);
    const nombre = f.owner_name ?? null;
    return [
        linea('Estado', ESTADOS[f.subscription_status]),
        nombre === null || nombre === '' ? linea('Propietario', f.owner_email ?? '—') : linea('Propietario', nombre, f.owner_email ?? null),
        linea('Ciudad', f.city ?? '—'),
        linea('Cliente desde', clienteDesde(f.created_at)),
        linea('Partner', f.partner_name ?? '—', f.partner_name !== null && f.partner_attribution_source === 'manual' ? 'Atribuido a mano' : null),
        linea('Cupón', f.coupon_code ?? '—'),
        linea('Equipo', f.member_count === undefined ? '—' : personas(f.member_count)),
        linea('Avisos abiertos', avisos ?? 'Ninguno', avisos === null ? null : 'En la cola de errores'),
    ];
}

const ORIGEN: Readonly<Record<SuscripcionDeNegocio['items'][number]['source'], string>> = {
    subscription: 'Contratado',
    included: 'Incluido sin coste',
    trial: 'En la prueba',
    platform_grant: 'Regalo de Reservaria',
    migration: 'De la versión anterior',
};

/**
 * Pestaña **Plan** (G138): la base primero y cada bloque, el cupón, el total al mes sin IVA (`monthly_fee`, G184) y el
 * resumen del plan. Los importes de cada bloque esperan al catálogo (M1-13): mientras, de dónde viene cada uno.
 */
export function lineasPlan(s: SuscripcionDeNegocio, f: FichaDeNegocio): Linea[] {
    const items = [...s.items].sort((a, b) => (a.product_code === 'base' ? -1 : b.product_code === 'base' ? 1 : 0));
    return [
        ...items.map((i) => linea(i.quantity > 1 ? `${nombreBloque(i.product_code)} × ${String(i.quantity)}` : nombreBloque(i.product_code), ORIGEN[i.source], null, true)),
        ...(s.coupon_code === null || s.coupon_code === undefined ? [] : [linea(`Cupón ${s.coupon_code}`, 'Aplicado', null, true)]),
        linea('Total al mes', totalAlMes(f.monthly_fee), 'Sin IVA', true),
        linea('Plan', f.plan_summary ?? '—'),
    ];
}

/** Pestaña **Equipo** (G138): nombre, roles y dos pasos; sin email ni teléfono. */
export function lineasEquipo(miembros: readonly MiembroDePlataforma[]): Linea[] {
    return miembros.map((m) => linea(m.display_name, m.roles.length > 0 ? conY(m.roles) : m.is_owner ? 'Propietario' : '—', m.two_factor_enabled ? 'Dos pasos activa' : 'Sin dos pasos', true));
}

const dosCifras = (n: number): string => String(n).padStart(2, '0');

/** «Hoy, 11:05» o «02/10, 18:40», en la hora de quien mira. */
export function cuandoAcceso(iso: string, ahora: Date): string {
    const d = new Date(iso);
    const hora = `${dosCifras(d.getHours())}:${dosCifras(d.getMinutes())}`;
    const hoy = d.getFullYear() === ahora.getFullYear() && d.getMonth() === ahora.getMonth() && d.getDate() === ahora.getDate();
    return hoy ? `Hoy, ${hora}` : `${dosCifras(d.getDate())}/${dosCifras(d.getMonth() + 1)}, ${hora}`;
}

/** Pestaña **Accesos** (G138): «Andrea Soto · Solo lectura», el motivo y cuándo. */
export function lineasAccesos(accesos: readonly AccesoDePlataforma[], ahora: Date): Linea[] {
    return accesos.map((a) => linea(`${a.opened_by_name ?? 'Reservaria'} · ${a.mode === 'impersonation' ? 'Suplantación' : 'Solo lectura'}`, cuandoAcceso(a.opened_at, ahora), a.reason, true));
}

export const NOTA_EQUIPO = 'Ver el equipo no abre un acceso, pero queda en la auditoría de plataforma y en la Actividad del negocio, con tu nombre. Solo nombres, roles y dos pasos: sin emails ni teléfonos.';
export const NOTA_ACCESOS = 'Cada acceso queda también en la Actividad del negocio, con el motivo y la persona real.';

export const PESTANAS: readonly { clave: Pestana; etiqueta: string; icono: NombreIcono }[] = [
    { clave: 'resumen', etiqueta: 'Resumen', icono: 'file-text' },
    { clave: 'plan', etiqueta: 'Plan', icono: 'receipt' },
    { clave: 'equipo', etiqueta: 'Equipo', icono: 'users' },
    { clave: 'accesos', etiqueta: 'Accesos', icono: 'headset' },
];
export type Pestana = 'resumen' | 'plan' | 'equipo' | 'accesos';

export function pestanaDe(valor: string | null): Pestana {
    return PESTANAS.some((p) => p.clave === valor) ? (valor as Pestana) : 'resumen';
}

// ---- Ventanas ----

/** El motivo tiene de 10 a 500 letras (`Reason` del contrato). */
export const motivoValido = (motivo: string): boolean => motivo.trim().length >= 10;
export const ERROR_MOTIVO = 'Escribe el motivo, al menos 10 letras.';
export const ERROR_CODIGO = 'Escribe los 6 números de tu aplicación.';
export const ERROR_CODIGO_CADUCADO = 'Ese código ya no vale. Escribe el que te muestra ahora tu aplicación (los códigos valen 5 minutos).';
export const codigoValido = (codigo: string): boolean => /^\d{6}$/.test(codigo);

// Provisional hasta C.62 (texto del aviso).
/** G1127: el motivo viaja en el aviso al propietario y queda en la auditoría. */
export const AVISO_DATOS_PERSONALES = 'No escribas datos personales en el motivo (nombres, teléfonos, emails…): viaja en el aviso al propietario y queda en la auditoría.';

export const TEXTO_ENTRAR = 'El propietario recibe un aviso al momento y lo ve en su Actividad. El acceso caduca a los 60 minutos.';

/** Nota de la ventana «Entrar en…» según el modo y el rol (prototipo `va.nota`). */
export function notaEntrar(suplantar: boolean, superadmin: boolean, propietario: string): string {
    if (suplantar) {
        return `Entras como el propietario, ${propietario}, y ves el panel como él. No puedes reembolsar, tocar la cuenta de cobro, cambiar contraseñas, transferir la propiedad ni exportar clientes.`;
    }
    return superadmin ? 'Ves todo lo que ve el propietario, sin poder cambiarlo.' : 'Entras en solo lectura. Para cambiar algo, el propietario te tiene que dar permiso desde su Actividad.';
}

/** Días que se añaden a la prueba (prototipo: 7, 14 o 30; el contrato admite de 1 a 60). */
export const DIAS_PRUEBA = [7, 14, 30] as const;

/** «Sala Malandar tiene 9 días de prueba por delante.» (días que quedan hasta `trial_ends_at`). */
export function textoPrueba(nombre: string, finPrueba: string | null | undefined, ahora: Date): string {
    if (finPrueba === null || finPrueba === undefined) {
        return `${nombre} está en prueba.`;
    }
    const dias = Math.max(0, Math.ceil((new Date(finPrueba).getTime() - ahora.getTime()) / 86_400_000));
    return dias === 1 ? `${nombre} tiene 1 día de prueba por delante.` : `${nombre} tiene ${String(dias)} días de prueba por delante.`;
}

const FORMA: Readonly<Record<NonNullable<PartnerDePlataforma['legal_form']>, string>> = { self_employed: 'Autónomo', company: 'Empresa' };

/** «Consultor · Empresa · código NOCTURNA · 3 negocios» (prototipo `line`; `business_count`, G1145). */
export function lineaPartner(p: PartnerDePlataforma): string {
    const negocios = p.business_count === null || p.business_count === undefined ? null : p.business_count === 1 ? '1 negocio' : `${String(p.business_count)} negocios`;
    return [textoTipo(p.type), p.legal_form === null || p.legal_form === undefined ? null : FORMA[p.legal_form], p.referral_code === null ? null : `código ${p.referral_code}`, negocios]
        .filter((x): x is string => x !== null && x !== '')
        .join(' · ');
}

/** Partners aprobados que no son el actual, por nombre o código. */
export function partnersQueCoinciden(partners: readonly PartnerDePlataforma[], actual: string | null, q: string): PartnerDePlataforma[] {
    const t = q.trim().toLocaleLowerCase('es');
    return partners.filter((p) => p.id !== actual && p.status === 'approved' && (t === '' || `${p.legal_name} ${p.referral_code ?? ''}`.toLocaleLowerCase('es').includes(t)));
}

/** Textos de la ventana de atribuir (prototipo `vp`). */
export function textosAtribuir(nombre: string, actual: string | null, manual: boolean, elegido: string | null): { titulo: string; texto: string; accion: string; efecto: string } {
    const sin = actual === null;
    return {
        titulo: sin ? `Atribuir ${nombre} a un partner` : `Cambiar el partner de ${nombre}`,
        texto: sin
            ? `${nombre} se dio de alta sin código de partner. Un negocio solo tiene un partner.`
            : `Ahora es de ${actual}${manual ? ' (atribuido a mano)' : ' (por su código)'}. Un negocio solo tiene un partner: el nuevo sustituye al actual.`,
        accion: sin ? 'Atribuir' : 'Cambiar de partner',
        efecto:
            elegido === null
                ? 'Elige un partner para ver qué cambia.'
                : `Desde hoy, lo que pague ${nombre} genera la comisión de ${elegido}.${sin ? '' : ` Las comisiones ya calculadas de ${actual} no cambian.`}`,
    };
}

export function textosQuitarPartner(nombre: string, partner: string): { titulo: string; texto: string } {
    return { titulo: `Quitar el partner de ${nombre}`, texto: `Deja de ser de ${partner}. Las comisiones ya generadas no cambian. Desde hoy lo que pague no genera comisión.` };
}

/** El texto de un error para el aviso breve: el de la API (`detail`), o el genérico. */
export function textoDeError(e: unknown, generico = 'No se ha podido hacer. Prueba de nuevo.'): string {
    if (typeof e === 'object' && e !== null && 'problema' in e) {
        const p = (e as { problema: { detail?: string; title?: string } }).problema;
        return p.detail ?? p.title ?? generico;
    }
    return generico;
}
